import React, { useEffect, useState, useMemo } from "react";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import XCloseIcon from "assets/icons/HeroIcons/XCloseIcon";
import { RootState } from "store";
import { useSelector } from "react-redux";
import {
  closeExifViewerModalAction,
  openImageLightBoxModalAction,
  openVideoLightBoxModalAction,
} from "store/modals/reducer.actions";
import { useDispatch } from "react-redux";
import apiLibrary from "services/api";
import { Toasts } from "view/components/Toasts";
import { useNavigate } from "react-router-dom";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { capitalCase } from "capital-case";
import Map from "./Map";
import { TailSpin } from "react-loader-spinner";
import useCustomBranding from "hooks/useCustomBranding";
import Button from "view/components/Button";
import ImageCarousel from "view/components/ImageCarousel";
import "view/components/ImageCarousel/ImageCarousel.css";
import { InformationCircleIcon } from "assets/icons/HeroIcons";
import { camelCase } from "change-case";

const style: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "10px",
  backgroundColor: "#ffff",
};

interface ExifViewerModalProps {}

export const ExifViewerModal: React.FC<ExifViewerModalProps> = () => {
  const { exifViewerModal } = useSelector((state: RootState) => state.modals);
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { primaryColor } = useCustomBranding();
  const [showExifData, setShowExifData] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [observationImages, setObservationImages] = useState<any[]>([]);

  const [gpsLocationDetails, setGpsLocationDetails] = useState<{
    latitude: any;
    longitude: any;
    showMap: boolean;
  }>({
    latitude: null,
    longitude: null,
    showMap: false,
  });
  const [exifData, setExifData] = useState({});

  const currentImage = useMemo(() => {
    if (!observationImages || observationImages.length === 0) return null;
    return observationImages[currentImageIndex];
  }, [observationImages, currentImageIndex]);

  const handleClose = () => {
    dispatch(closeExifViewerModalAction());
    setShowExifData(false);
    setCurrentImageIndex(0);
    setObservationImages([]);
    setExifData({});
    setGpsLocationDetails({
      latitude: null,
      longitude: null,
      showMap: false,
    });
  };

  const navigate = useNavigate();

  // Helper function to remove repeated prefixes from a string

  // Function to convert DMS (Degrees, Minutes, Seconds) format to Decimal
  const convertDmsToDecimal = (
    degrees: number,
    minutes: number,
    secondsNumerator: number,
    secondsDenominator: number
  ): number => {
    const seconds = secondsNumerator / secondsDenominator;
    const decimal = degrees + minutes / 60 + seconds / 3600;
    return decimal;
  };

  // Function to remove prefixes like GPS, EXIF, etc. (including repeated occurrences)
  const removePrefixes = (str: string): string => {
    const prefixes = ["GPS", "EXIF", "Image", "Thumbnail", "Interoperability"];
    let result = str;

    // Loop through prefixes to remove them if they appear at the beginning of the string
    prefixes.forEach((prefix) => {
      const regex = new RegExp(`^${prefix}\\s*`, "i"); // Case-insensitive
      while (regex.test(result)) {
        result = result.replace(regex, ""); // Remove the prefix if it appears at the start
      }
    });

    return result;
  };

  // Function to process DMS formatted string into decimal
  const processCoordinateString = (coordStr: string): string | number => {
    // Check if the string matches the DMS format
    const regex = /^\[(\d+), (\d+), (\d+)\/(\d+)\]$/;
    const match = coordStr.match(regex);

    if (match) {
      const degrees = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      const secondsNumerator = parseInt(match[3]);
      const secondsDenominator = parseInt(match[4]);

      // Convert to decimal
      return convertDmsToDecimal(
        degrees,
        minutes,
        secondsNumerator,
        secondsDenominator
      );
    } else {
      return coordStr; // Return original value if it's not in DMS format
    }
  };

  // Function to remove repetitive prefixes and convert keys to camel case
  const removeRepetitivePrefixesAndConvertToCamelCase = (
    obj: Record<string, any>
  ): Record<string, any> => {
    const newObj: Record<string, any> = {};

    // Loop through the original object's keys
    Object.keys(obj).forEach((key) => {
      let transformedKey = removePrefixes(key); // Remove repeated prefixes

      // Convert the key to camel case
      transformedKey = camelCase(transformedKey);

      // Process coordinates if the value matches DMS format
      if (typeof obj[key] === "string") {
        const processedValue = processCoordinateString(obj[key]);
        newObj[transformedKey] = processedValue; // Add to new object with processed value
      } else {
        newObj[transformedKey] = obj[key]; // If it's not a string, just copy the value
      }
    });

    return newObj;
  };
  // Get EXIF data of media files (image/video)
  const getExifDataOfMediaFiles = async (id: number) => {
    setIsLoading(true);

    try {
      const { message, data } = await apiLibrary.Observations.getExifData(id);
      const updatedData = removeRepetitivePrefixesAndConvertToCamelCase(
        data === null ? {} : data
      );
      // If GPS coordinates are available, convert them to decimal degrees
      if (updatedData && updatedData.longitude && updatedData.latitude) {
        const longitude = convertCoordinateToDecimal(
          updatedData.longitude.toString()
        );
        const latitude = convertCoordinateToDecimal(
          updatedData.latitude.toString()
        );
        if (
          longitude !== "invalidCoordinates" &&
          latitude !== "invalidCoordinates"
        ) {
          setGpsLocationDetails({
            latitude: latitude,
            longitude: longitude,
            showMap: true,
          });
        } else {
          setGpsLocationDetails({
            latitude: null,
            longitude: null,
            showMap: false,
          });
        }
      } else {
        setGpsLocationDetails({
          latitude: null,
          longitude: null,
          showMap: false,
        });
      }

      if (updatedData) {
        setExifData(updatedData);
        Toasts.success("Exif data retrieved Successfully");
      } else {
        setExifData({});
        Toasts.success(message);
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error.message;
      Toasts.error(errorMsg);
      setExifData({});
      setGpsLocationDetails({
        latitude: null,
        longitude: null,
        showMap: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewExifData = () => {
    if (!showExifData) {
      // Only make API call when showing EXIF data for the first time
      const currentImage = observationImages[currentImageIndex];
      const fileId =
        currentImage?.id ||
        (currentImage?.url && extractFileId(currentImage.url));
      if (fileId && currentImage.dataType === "image") {
        getExifDataOfMediaFiles(fileId);
      }
      setShowExifData(true);
    } else {
      // Just hide EXIF data, no API call needed
      setShowExifData(false);
    }
  };

  const handleCarouselChange = (index: number) => {
    const previousIndex = currentImageIndex;
    setCurrentImageIndex(index);

    // Only load EXIF data if:
    // 1. EXIF data is currently shown
    // 2. The index actually changed
    // 3. We're not in a loading state
    if (showExifData && index !== previousIndex && !isLoading) {
      const newImage = observationImages[index];
      const fileId =
        newImage?.id || (newImage?.url && extractFileId(newImage.url));
      if (fileId) {
        getExifDataOfMediaFiles(fileId);
      }
    }
  };

  const handleImageClick = (currentImage: any) => {
    if (currentImage?.dataType === "image") {
      dispatch(
        openImageLightBoxModalAction({
          width: 1080,
          filePath: currentImage.url,
        })
      );
    } else if (currentImage?.dataType === "video") {
      dispatch(
        openVideoLightBoxModalAction({
          filePath: currentImage.url,
          fileType: currentImage.fileType,
        })
      );
    } else {
      console.warn("Unsupported or missing dataType:", currentImage?.dataType);
    }
  };

  // Initialize images when modal opens
  useEffect(() => {
    if (exifViewerModal?.isOpen && exifViewerModal.data) {
      if (
        exifViewerModal.data.allImages &&
        exifViewerModal.data.allImages.length > 0
      ) {
        setObservationImages(exifViewerModal.data.allImages);

        const passedIndex = exifViewerModal.data.currentImageIndex;
        if (typeof passedIndex === "number" && passedIndex >= 0) {
          setCurrentImageIndex(passedIndex);
        } else {
          const clickedImageIndex = exifViewerModal.data.allImages.findIndex(
            (img: any) =>
              img.url === exifViewerModal.data.fileUrl ||
              img.id === exifViewerModal.data.fileId
          );
          setCurrentImageIndex(clickedImageIndex >= 0 ? clickedImageIndex : 0);
        }
      } else {
        const singleImage = {
          id: exifViewerModal.data.fileId || 1,
          url: exifViewerModal.data.fileUrl,
          fileType: exifViewerModal.data.fileType,
          dataType: exifViewerModal.data.dataType,
        };
        setObservationImages([singleImage]);
        setCurrentImageIndex(0);
      }
    }
  }, [exifViewerModal?.isOpen, exifViewerModal.data]);

  // Reset states when modal closes
  useEffect(() => {
    if (!exifViewerModal?.isOpen) {
      setShowExifData(false);
      setCurrentImageIndex(0);
      setObservationImages([]);
      setExifData({});
      setGpsLocationDetails({
        latitude: null,
        longitude: null,
        showMap: false,
      });
    }
  }, [exifViewerModal?.isOpen]);

  return (
    <>
      <Modal
        open={exifViewerModal?.isOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        disableAutoFocus={true}
        aria-describedby="modal-modal-description"
        className="border-none"
      >
        <Box sx={style} className="dark:bg-secondaryLight">
          <div
            className={`flex flex-col ${showExifData ? "w-[1200px]" : "w-[1200px]"} rounded-lg transition-all duration-300 bg-white dark:bg-secondaryLight`}
          >
            <div
              className="relative flex flex-col p-6 rounded-lg"
              style={{
                boxShadow:
                  "0px 2px 8px 0 rgba(2,13,36,0.1), 0px 2px 24px 0 rgba(2,13,36,0.08)",
              }}
            >
              {/* Header */}
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                  {/* Image Viewer */}
                </h2>
                <button
                  onClick={handleClose}
                  type="button"
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <XCloseIcon width={24} height={24} viewBox="0 0 24 24" />
                </button>
              </div>

              {/* Main Content */}
              <div className="relative flex justify-center gap-6">
                {/* Image Carousel Section */}
                <ImageCarousel
                  images={observationImages}
                  currentIndex={currentImageIndex}
                  showExifData={showExifData}
                  onImageChange={handleCarouselChange}
                  onImageClick={(item: any) => {
                    handleImageClick(item);
                  }}
                />

                <div className="flex flex-col items-end">
                  {/* EXIF Data Section */}
                  {showExifData && (
                    <div className="w-[353px] flex flex-col">
                      {isLoading ? (
                        <div className="flex justify-center items-center max-h-[400px]">
                          <TailSpin
                            height="50"
                            width="50"
                            color={primaryColor || "#1976d2"}
                            ariaLabel="tail-spin-loading"
                            radius="2"
                            wrapperStyle={{}}
                            wrapperClass="tailspin-loader"
                            visible={true}
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col h-full mt-6">
                          {/* EXIF Data Table */}
                          <div className="flex-1 overflow-y-auto max-h-[420px] mb-3 font-normal">
                            {Object.entries(exifData).length > 0 && (
                              <div className="exif-data-table">
                                {Object.entries(exifData)
                                  .filter(
                                    ([key, value]) =>
                                      value !== null &&
                                      value !== undefined &&
                                      value !== ""
                                  )
                                  ?.map(([key, value], index) => {
                                    const capitalizedKey = capitalCase(
                                      key ?? ""
                                    );
                                    const cleanedKey = capitalizedKey.replace(
                                      /exif/gi,
                                      ""
                                    );

                                    return (
                                      <div key={index} className="exif-row">
                                        <div className="exif-key bg-primaryExtraLight">
                                          {cleanedKey}
                                        </div>
                                        <div className="exif-value">
                                          {typeof value === "string" ||
                                          typeof value === "number"
                                            ? value
                                            : typeof value === "undefined"
                                              ? ""
                                              : JSON.stringify(value)}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>

                          {/* Location Section */}
                          {gpsLocationDetails.showMap && (
                            <div className="flex-shrink-0 mb-4">
                              <div className="h-[90px] rounded-lg overflow-hidden border">
                                <Map
                                  coordiantes={[
                                    gpsLocationDetails.longitude,
                                    gpsLocationDetails.latitude,
                                  ]}
                                  editMode={false}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* View EXIF Data Button */}
                  {currentImage && currentImage.dataType === "image" && (
                    <div className="w-full absolute left-0 bottom-0 ml-auto float-left">
                      <Button
                        type="button"
                        text={
                          showExifData ? "Close EXIF Data" : "View EXIF Data"
                        }
                        icon={
                          showExifData ? (
                            <XCloseIcon fill="white" />
                          ) : (
                            <InformationCircleIcon fill="white" />
                          )
                        }
                        filledColor="primary"
                        outlinedColor="primary"
                        textColor="textWhite"
                        className="px-5 py-2 border-2 border-primary ml-auto"
                        width="60"
                        height="13"
                        fontStyle="font-normal"
                        variant="filled"
                        onClick={handleViewExifData}
                        disabled={isLoading}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
};

function convertCoordinateToDecimal(coordinate: any) {
  coordinate = coordinate.trim();
  if (isDecimalDegrees(coordinate)) {
    return parseFloat(coordinate);
  } else if (isDMS(coordinate)) {
    return convertDMSToDecimal(coordinate);
  } else if (isDDM(coordinate)) {
    return convertDDMToDecimal(coordinate);
  } else {
    return "invalidCoordinates";
  }
}

function isDecimalDegrees(coordinate: any) {
  const decimalRegex = /^-?\d+(\.\d+)?$/;
  return decimalRegex.test(coordinate.trim());
}

function isDMS(coordinate: any) {
  const dmsRegex =
    /^\d{1,3}\s*(°|deg)\s*\d{1,2}'\s*\d{1,2}(\.\d+)?\"\s*([NSEW])?$/i;
  return dmsRegex.test(coordinate.trim());
}

function isDDM(coordinate: any) {
  const ddmRegex = /^\d{1,3}\s*(°|deg)\s*\d{1,2}(\.\d+)?'\s*([NSEW])?$/i;
  return ddmRegex.test(coordinate.trim());
}

function convertDDMToDecimal(ddmString: string) {
  const ddmRegex = /(\d{1,3})\s*(°|deg)\s*(\d{1,2}(\.\d+)?)'\s*([NSEW])?/i;
  const matches = ddmString.match(ddmRegex);

  if (!matches) {
    return "invalidCoordinates";
  }

  const degrees = parseInt(matches[1], 10);
  const decimalMinutes = parseFloat(matches[3]);
  const direction = matches[5] ? matches[5].toUpperCase() : null;

  let decimalDegrees = degrees + decimalMinutes / 60;

  if (direction === "S" || direction === "W") {
    decimalDegrees *= -1;
  }

  return decimalDegrees;
}

function convertDMSToDecimal(dmsString: string) {
  const dmsRegex =
    /(\d{1,3})\s*(°|deg)\s*(\d{1,2})'\s*(\d{1,2}(\.\d+)?)\"\s*([NSEW])/i;
  const matches = dmsString.match(dmsRegex);

  if (!matches) {
    return "invalidCoordinates";
  }

  const degrees = parseInt(matches[1], 10);
  const minutes = parseInt(matches[3], 10);
  const seconds = parseFloat(matches[4]);
  const direction = matches[6].toUpperCase();

  let decimalDegrees = degrees + minutes / 60 + seconds / 3600;

  if (direction === "S" || direction === "W") {
    decimalDegrees *= -1;
  }

  return decimalDegrees;
}

function extractFileId(imageLink: string) {
  try {
    const url = new URL(imageLink);
    return url.searchParams.get("fileId");
  } catch (error) {
    console.error("Invalid URL provided:", error);
    return null;
  }
}
